// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import eyeLogo from "src/assets/images/logos/eyelogo.svg";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { guideTarget } from "../Walkthrough/walkthroughState";

const WINDOWS_FONT = "'Segoe UI', Tahoma, Arial, sans-serif";

const target = (id?: string) => (id ? guideTarget(id) : {});

const ZipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 2h8l4 4v12H4z" fill="#F7D774" stroke="#C9A227" />
    <path d="M9 3v8M11 4v8" stroke="#555" strokeDasharray="1 1" />
  </svg>
);

const MsiIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
    <rect x="3" y="3" width="14" height="14" rx="2" fill="#0078D7" />
    <path d="M6 10h8M10 6v8" stroke="#FFF" strokeWidth="1.6" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 2h8l4 4v12H4z" fill="#FFF" stroke="#888" />
    <path d="M7 10h6M7 13h6" stroke="#888" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 1l6 2v5c0 3.5-2.6 6-6 7-3.4-1-6-3.5-6-7V3z" fill="#2B7CD3" />
    <path d="M8 1v14c3.4-1 6-3.5 6-7V3z" fill="#F2C335" />
  </svg>
);

const WindowsButton = ({
  children,
  isDefault = false,
  isDisabled = false,
  targetId,
}: {
  children: ReactNode;
  isDefault?: boolean;
  isDisabled?: boolean;
  targetId?: string;
}) => (
  <Box
    component="span"
    {...target(targetId)}
    sx={{
      fontSize: "12px",
      backgroundColor: "#E1E1E1",
      border: isDefault ? "2px solid #0078D7" : "1px solid #ADADAD",
      minWidth: "76px",
      height: "26px",
      padding: "0 10px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "5px",
      color: isDisabled ? "#A0A0A0" : "#000",
    }}
  >
    {children}
  </Box>
);

const WindowFrame = ({
  title,
  maxWidth = "520px",
  children,
}: {
  title: string;
  maxWidth?: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      fontFamily: WINDOWS_FONT,
      color: "#000",
      backgroundColor: "#F0F0F0",
      border: "1px solid #7A7A7A",
      width: "100%",
      maxWidth,
      margin: "0 auto",
      boxShadow: "0 12px 30px rgba(0, 0, 0, 0.25)",
    }}
  >
    <Box
      sx={{
        backgroundColor: "#FFF",
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 10px",
        fontSize: "12px",
        borderBottom: "1px solid #E5E5E5",
      }}
    >
      <span>{title}</span>
      <span>✕</span>
    </Box>
    {children}
  </Box>
);

const InstallerFooter = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      gap: "8px",
      padding: "10px 12px",
      borderTop: "1px solid #D9D9D9",
      flexWrap: "wrap",
    }}
  >
    {children}
  </Box>
);

const SETUP_TITLE = "RetinaLogik EMR Hub Setup";

export const InstallerWelcome = ({
  heading,
  lines,
  lineTarget,
  nextLabel = "Next",
  nextTarget,
}: {
  heading: string;
  lines: Array<string>;
  lineTarget?: string;
  nextLabel?: "Next" | "Finish";
  nextTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <WindowFrame title={t(SETUP_TITLE)}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "60px minmax(0, 1fr)",
            sm: "150px minmax(0, 1fr)",
          },
          minHeight: "230px",
          backgroundColor: "#FFF",
        }}
      >
        <Box sx={{ background: "linear-gradient(160deg, #202338, #1B8F76)" }} />
        <Box
          sx={{
            padding: "18px",
            fontSize: "12.5px",
            lineHeight: 1.55,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <Box component="b" fontSize="17px" fontWeight={600}>
            {t(heading)}
          </Box>
          {lines.map((line, index) => (
            <span
              key={line}
              {...(index === lines.length - 1 ? target(lineTarget) : {})}
            >
              {t(line)}
            </span>
          ))}
        </Box>
      </Box>
      <InstallerFooter>
        <WindowsButton isDisabled>{t("Back")}</WindowsButton>
        <WindowsButton isDefault targetId={nextTarget}>
          {t(nextLabel)}
        </WindowsButton>
        <WindowsButton isDisabled={nextLabel === "Finish"}>
          {t("Cancel")}
        </WindowsButton>
      </InstallerFooter>
    </WindowFrame>
  );
};

export const InstallerReady = ({
  messageTarget,
  installTarget,
}: {
  messageTarget?: string;
  installTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <WindowFrame title={t(SETUP_TITLE)}>
      <Box
        sx={{
          backgroundColor: "#FFF",
          padding: "14px 16px",
          borderBottom: "1px solid #D9D9D9",
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <Box>
          <Box component="b" display="block" fontSize="14px">
            {t("Ready to install")}
          </Box>
          <Box component="span" fontSize="12px" color="#333">
            {t("Setup checked the enrollment token with RetinaLogik.")}
          </Box>
        </Box>
        <Box component="img" src={eyeLogo} alt="" sx={{ width: 44 }} />
      </Box>
      <Box
        sx={{
          padding: "16px",
          fontSize: "12.5px",
          lineHeight: 1.5,
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <span {...target(messageTarget)}>
          {t("This PC will be enrolled as Front desk PC for Maple Eye Clinic.")}
        </span>
        <span>
          {t(
            "Select Install to install the hub and enroll this PC. Windows may ask for permission to make changes."
          )}
        </span>
      </Box>
      <InstallerFooter>
        <WindowsButton>{t("Back")}</WindowsButton>
        <WindowsButton isDefault targetId={installTarget}>
          <ShieldIcon />
          {t("Install")}
        </WindowsButton>
        <WindowsButton>{t("Cancel")}</WindowsButton>
      </InstallerFooter>
    </WindowFrame>
  );
};

const ExplorerRow = ({
  icon,
  name,
  type,
  isSelected = false,
  targetId,
}: {
  icon: ReactNode;
  name: string;
  type: string;
  isSelected?: boolean;
  targetId?: string;
}) => (
  <Box
    {...target(targetId)}
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 120px",
      gap: "10px",
      padding: "7px 10px",
      alignItems: "center",
      backgroundColor: isSelected ? "#CCE8FF" : "transparent",
    }}
  >
    <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
      {icon}
      <Box component="span" sx={{ overflowWrap: "anywhere" }}>
        {name}
      </Box>
    </Box>
    <span>{type}</span>
  </Box>
);

const Explorer = ({
  title,
  path,
  children,
}: {
  title: string;
  path: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Box
      sx={{
        fontFamily: WINDOWS_FONT,
        color: "#000",
        backgroundColor: "#FFF",
        border: "1px solid #7A7A7A",
        maxWidth: "560px",
        margin: "0 auto",
        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.25)",
        fontSize: "12.5px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "7px 10px",
          backgroundColor: "#F3F3F3",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <span>{title}</span>
        <span>✕</span>
      </Box>
      <Box
        sx={{
          padding: "6px 10px",
          borderBottom: "1px solid #E0E0E0",
          color: "#333",
          backgroundColor: "#FAFAFA",
        }}
      >
        {path}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 120px",
          gap: "10px",
          padding: "7px 10px",
          color: "#555",
          fontSize: "11.5px",
          borderBottom: "1px solid #EEE",
        }}
      >
        <span>{t("Name")}</span>
        <span>{t("Type")}</span>
      </Box>
      {children}
      <Box height="50px" />
    </Box>
  );
};

export const DownloadsFolder = ({
  showMenu,
  zipTarget,
  extractTarget,
}: {
  showMenu: boolean;
  zipTarget?: string;
  extractTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Box position="relative">
      <Explorer title={t("Downloads")} path={t("This PC › Downloads")}>
        <ExplorerRow
          icon={<ZipIcon />}
          name="EmrHub-front-desk-pc.zip"
          type={t("Compressed folder")}
          isSelected
          targetId={zipTarget}
        />
        <ExplorerRow
          icon={<DocumentIcon />}
          name="invoice-october.pdf"
          type={t("PDF document")}
        />
      </Explorer>
      {showMenu && (
        <Box
          sx={{
            position: "absolute",
            left: "46%",
            top: "58px",
            backgroundColor: "#F9F9F9",
            border: "1px solid #CCC",
            boxShadow: "2px 4px 12px rgba(0, 0, 0, 0.2)",
            fontFamily: WINDOWS_FONT,
            fontSize: "12px",
            color: "#000",
            minWidth: "170px",
            zIndex: 3,
            "& > div": { padding: "6px 14px" },
          }}
        >
          <div>{t("Open")}</div>
          <Box {...target(extractTarget)} sx={{ backgroundColor: "#E5F3FF" }}>
            {t("Extract All...")}
          </Box>
          <div>{t("Copy")}</div>
          <div>{t("Properties")}</div>
        </Box>
      )}
    </Box>
  );
};

export const ExtractedFolder = ({
  msiTarget,
  jsonTarget,
}: {
  msiTarget?: string;
  jsonTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Explorer
      title="EmrHub-front-desk-pc"
      path={`${t("This PC › Downloads")} › EmrHub-front-desk-pc`}
    >
      <ExplorerRow
        icon={<MsiIcon />}
        name="EmrHub.msi"
        type={t("Windows Installer")}
        targetId={msiTarget}
      />
      <ExplorerRow
        icon={<DocumentIcon />}
        name="enrollment.json"
        type={t("JSON file")}
        targetId={jsonTarget}
      />
    </Explorer>
  );
};

const DARK = {
  window: "#1C1C1C",
  card: "#2B2B2B",
  border: "#3A3A3A",
  muted: "#9D9D9D",
  soft: "#C5C5C5",
  accent: "#4CC2FF",
};

export const QuickLinkMenu = ({
  startTarget,
  installedAppsTarget,
}: {
  startTarget?: string;
  installedAppsTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const items = [
    "Mobility Center",
    "Power Options",
    "Event Viewer",
    "System",
    "-",
    "Task Manager",
    "Settings",
    "File Explorer",
    "Search",
    "Run",
    "-",
    "Shut down or sign out",
    "Desktop",
  ];

  return (
    <Box fontFamily={WINDOWS_FONT}>
      <Box
        sx={{
          backgroundColor: DARK.card,
          color: "#FFF",
          border: `1px solid ${DARK.border}`,
          borderRadius: "8px",
          maxWidth: "360px",
          margin: "0 auto",
          padding: "4px 0",
          fontSize: "13px",
          boxShadow: "0 12px 30px rgba(0, 0, 0, 0.35)",
          "& > div": { padding: "6px 14px" },
        }}
      >
        <Box
          {...target(installedAppsTarget)}
          sx={{ backgroundColor: DARK.border }}
        >
          {t("Installed apps")}
        </Box>
        {items.map((item, index) =>
          item === "-" ? (
            <Box
              key={`divider-${index}`}
              sx={{
                borderTop: "1px solid #444",
                padding: "0 !important",
                margin: "4px 0",
              }}
            />
          ) : (
            <div key={item}>{t(item)}</div>
          )
        )}
      </Box>
      <Box display="flex" justifyContent="center" mt="10px">
        <Box
          {...target(startTarget)}
          sx={{
            width: "36px",
            height: "36px",
            display: "grid",
            placeItems: "center",
            backgroundColor: DARK.card,
            border: `1px solid ${DARK.border}`,
            borderRadius: "4px",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <rect x="0" y="0" width="8" height="8" fill={DARK.accent} />
            <rect x="10" y="0" width="8" height="8" fill={DARK.accent} />
            <rect x="0" y="10" width="8" height="8" fill={DARK.accent} />
            <rect x="10" y="10" width="8" height="8" fill={DARK.accent} />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

const OldAppIcon = () => (
  <Box
    sx={{
      width: "30px",
      height: "30px",
      backgroundColor: "#FFF",
      display: "grid",
      placeItems: "center",
      borderRadius: "2px",
      flexShrink: 0,
    }}
  >
    <svg width="18" height="12" viewBox="0 0 18 12" aria-hidden="true">
      <path
        d="M1 6c2-3.3 4.7-5 8-5s6 1.7 8 5c-2 3.3-4.7 5-8 5S3 9.3 1 6z"
        fill="none"
        stroke="#E2772E"
        strokeWidth="1.6"
      />
      <circle cx="9" cy="6" r="2.3" fill="#E2772E" />
    </svg>
  </Box>
);

const flyoutSx = {
  position: "absolute",
  right: "16px",
  top: "calc(100% - 6px)",
  backgroundColor: "#2C2C2C",
  border: "1px solid #454545",
  borderRadius: "8px",
  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.5)",
  zIndex: 3,
};

export const InstalledApps = ({
  isSearched,
  isRemoved,
  showMenu,
  showConfirm,
  searchTarget,
  nameTarget,
  dotsTarget,
  uninstallTarget,
  confirmTarget,
}: {
  isSearched: boolean;
  isRemoved: boolean;
  showMenu: boolean;
  showConfirm: boolean;
  searchTarget?: string;
  nameTarget?: string;
  dotsTarget?: string;
  uninstallTarget?: string;
  confirmTarget?: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const showsApp = isSearched && !isRemoved;

  return (
    <Box
      sx={{
        fontFamily: WINDOWS_FONT,
        backgroundColor: DARK.window,
        color: "#FFF",
        border: `1px solid ${DARK.border}`,
        borderRadius: "8px",
        padding: "18px",
        maxWidth: "760px",
        margin: "0 auto",
        fontSize: "13px",
        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "14px",
        }}
      >
        <Box component="span" color={DARK.muted}>
          {t("Apps")}
        </Box>
        ›<span>{t("Installed apps")}</span>
      </Box>
      <Box
        {...target(searchTarget)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#2D2D2D",
          border: `1px solid ${DARK.border}`,
          borderBottom: `1px solid ${DARK.muted}`,
          borderRadius: "4px",
          padding: "7px 10px",
          maxWidth: "340px",
          minHeight: "34px",
        }}
      >
        <Box component="span" color={isSearched ? "#FFF" : DARK.muted}>
          {isSearched ? "Retina" : t("Search apps")}
        </Box>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke={DARK.soft}
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M15 15l6 6" />
        </svg>
      </Box>
      {isSearched && (
        <Box margin="16px 0 10px">
          {isRemoved ? t("0 apps found") : t("1 app found")}
        </Box>
      )}
      {showsApp && (
        <Box position="relative" paddingBottom="96px">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              backgroundColor: DARK.card,
              borderRadius: "6px",
              padding: "14px 16px",
              flexWrap: "wrap",
              position: "relative",
            }}
          >
            <OldAppIcon />
            <Box flex="1 1 160px" minWidth={0}>
              <Box
                {...target(nameTarget)}
                fontSize="14px"
                display="inline-block"
              >
                RetinaLogik EMR
              </Box>
              <Box fontSize="11.5px" color={DARK.soft}>
                1.0.0.0 | RetinaLogik Inc. | 5/4/2026
              </Box>
            </Box>
            <span>17.0 MB</span>
            <Box
              {...target(dotsTarget)}
              sx={{
                width: "32px",
                height: "32px",
                display: "grid",
                placeItems: "center",
                borderRadius: "4px",
                fontWeight: 700,
                letterSpacing: "1px",
              }}
            >
              ···
            </Box>
            {showMenu && (
              <Box sx={{ ...flyoutSx, padding: "4px", minWidth: "150px" }}>
                <Box padding="7px 12px" color="#7A7A7A">
                  {t("Modify")}
                </Box>
                <Box
                  {...target(uninstallTarget)}
                  sx={{
                    padding: "7px 12px",
                    borderRadius: "4px",
                    backgroundColor: DARK.border,
                  }}
                >
                  {t("Uninstall")}
                </Box>
              </Box>
            )}
            {showConfirm && (
              <Box
                sx={{
                  ...flyoutSx,
                  padding: "14px",
                  width: "min(290px, 90%)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <span>
                  {t("This app and its related info will be uninstalled.")}
                </span>
                <Box
                  {...target(confirmTarget)}
                  sx={{
                    alignSelf: "flex-end",
                    backgroundColor: DARK.accent,
                    color: "#000",
                    borderRadius: "4px",
                    padding: "6px 18px",
                  }}
                >
                  {t("Uninstall")}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const UserAccountControl = ({ yesTarget }: { yesTarget?: string }) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Box
      sx={{
        fontFamily: WINDOWS_FONT,
        maxWidth: "440px",
        margin: "0 auto",
        backgroundColor: "#202020",
        color: "#FFF",
        border: `1px solid ${DARK.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.4)",
      }}
    >
      <Box
        sx={{
          backgroundColor: DARK.card,
          padding: "10px 16px",
          fontSize: "12px",
        }}
      >
        {t("User Account Control")}
      </Box>
      <Box
        sx={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          fontSize: "13px",
        }}
      >
        <Box component="b" fontSize="18px" fontWeight={600}>
          {t("Do you want to allow this app to make changes to your device?")}
        </Box>
        <Box display="flex" gap="10px" alignItems="center">
          <MsiIcon size={28} />
          <span>{t("Windows Installer")}</span>
        </Box>
        <Box component="span" color={DARK.soft}>
          {t("Verified publisher: Microsoft Windows")}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          padding: "0 16px 16px",
        }}
      >
        <Box
          {...target(yesTarget)}
          sx={{
            textAlign: "center",
            padding: "7px",
            borderRadius: "4px",
            backgroundColor: DARK.accent,
            color: "#000",
          }}
        >
          {t("Yes")}
        </Box>
        <Box
          sx={{
            textAlign: "center",
            padding: "7px",
            borderRadius: "4px",
            backgroundColor: DARK.border,
          }}
        >
          {t("No")}
        </Box>
      </Box>
    </Box>
  );
};
